import { useMemo } from 'react';

import { useSlotBattle } from '../../../hooks/useSlotBattle';
import { useStores } from '../../../stores/useStores';
import type { WidgetEntry } from '../types';

/**
 * Tie-breaker inside the SlotBattle priority bucket. Only one battle can be live per coin type, so
 * this only matters against widgets that share the bucket (Challenge today).
 */
const SLOT_BATTLE_WIDGET_WEIGHT = 200;

/**
 * Lobby rail entry for Battle of the Slots (GS-813).
 *
 * Two gates before a slot is taken: the coin-scoped read must have produced a battle with widget art
 * in a state that still has something to open (`canShowWidget`), and the run's LaunchDarkly flag must
 * still place this player in a segment (`useSlotBattle`). Either one failing means the rail simply
 * carries one fewer widget, so an art-less run never counts toward the rail's layout.
 */
export function useSlotBattleWidgets() {
  const { slotBattleStore } = useStores();
  const battle = useSlotBattle();
  const { canShowWidget } = slotBattleStore;
  // Not the payload object itself: it is replaced on every poll, and a new array identity per poll
  // would re-sort the whole rail.
  const runKey = battle?.runKey ?? null;

  return useMemo((): WidgetEntry<'slotBattle'>[] => {
    if (!runKey || !canShowWidget) {
      return [];
    }

    return [
      {
        data: { key: 'slot-battle', dataTestId: 'slot-battle-widget', weight: SLOT_BATTLE_WIDGET_WEIGHT },
        type: 'slotBattle',
        id: 'slot-battle',
      },
    ];
  }, [runKey, canShowWidget]);
}
